/**
 * CallHistoryDetails.tsx
 * Talkroute
 *
 * Created by Vel Genov on 1/2/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

 import { ADD_CONTACT_TOOLTIP_LABEL, CONTACTS_INITIAL_PAGE, GET_TEN_THOUSAND_CONTACTS } from "@components/Contacts/constants";
import ContactDialog from "@components/Contacts/ContactDialog";
import CreateContactDialog from "@components/Contacts/CreateContactDialog";
import Tooltip from "@components/PresentationComponents/FormComponents/Tooltip";
import { domIds } from "@constants/testConstants";
import { addContactNameToCallHistory } from "@helpers/functions/contacts";
import { trimPhoneNumber } from "@helpers/functions/phoneNumberFormatter";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import { IconButton } from '@material-ui/core';
import { CallType } from "@models/Callhistory.models";
import { ContactPhoneTypeEnum, IContact } from "@models/Contacts.models";
import CreateContactIcon from "@resources/icons/ic-add-contact.svg";
import AddContactIcon from "@resources/icons/phone-plus.svg";
import CallBlockIcon from "@resources/icons/top-nav-block.svg";
import CallBackIcon from "@resources/icons/top-nav-call-back.svg";
import TextBackIcon from "@resources/icons/top-nav-text-back.svg";
import CallUnblockIcon from "@resources/icons/top-nav-unblock.svg";
import { useGetAllContactsQuery } from "@services/kingcobraApi";
import { push } from "connected-react-router";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { Grid, List } from "semantic-ui-react";
import {
  blockNumber,
  fetchBlockedNumbers,
  setNumberToCall,
  setOutgoingPhoneNumber,
  unblockNumber
} from "../../actions/callActions";
import { hideCallDetails } from "../../actions/callHistoryActions";
import {
  createNewConversation,
  updateNewConversationPhoneNumber
} from "../../actions/conversationActions";
import { getUserPermissions } from "../../actions/userActions";
import routes from "../../constants/routes";
import uiString from "../../constants/uiString";
import userConstants from "../../constants/userConstants";
import CallDetails from "../../containers/CallDetails";
import { formatSecondsWithQualifier } from "../../helpers/converters";
import { ThunkStatus } from "../../models/Redux.models";
import ConfirmModal from "../modal/ConfirmModal";
import { getEditableForNumber } from "@services/dataClasses/TalkroutePhoneNumber";
 
 const CANT_BLOCK_NUMBER_MESSAGE = "Number can not be blocked at this time";
 const BLOCK_NUMBER_SUCCESS = "The number has been blocked."
 const UNBLOCK_NUMBER_SUCCESS = "The number has been unblocked."
 
 const buttonStyles = (isDisabled?: boolean) => ({
   padding: 10,
   bottom: 2,
   right: 2,
   opacity: isDisabled ? .45 : 1
 })
 
 /**
  * A class based component that takes in data about a single call from Redux and renders it to the screen.
  * This class is designed to show data about 1 call at a time
  */
 const CallHistoryDetails: FC = () => {
   const [isShowBlockPermissionError, setIsShowBlockPermissionError] = useState(false);
   const outgoingPhoneNumbers = useAppSelector(state => state.preferences.outgoingPhoneNumbers);
   const outgoingTextPhoneNumbers = useAppSelector(state => state.preferences.outgoingTextPhoneNumbers);
   const callDetails = useAppSelector(state => state.callHistory.callDetails);
   const relatedCalls = useAppSelector(state => state.callHistory.relatedCalls);
   const parent = useAppSelector(state => state.callHistory.callDetailsParent);
   const blockedNumbers = useAppSelector(state => state.callReducer.blockedNumbers);
   const blockNumberStatus = useAppSelector(state => state.callReducer.blockNumberStatus);
   const userPermissions = useAppSelector(state => state.authentication.userPermissions);
   const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
   const [isCreateContactDialogOpen, setIsCreateContactDialogOpen] = useState(false);
 
   const {data: contacts, refetch, isFetching} = useGetAllContactsQuery({ params: { 
     pageSize: GET_TEN_THOUSAND_CONTACTS,
     page: CONTACTS_INITIAL_PAGE
   } });
 
   const attachedContact = useMemo(
     () => contacts && callDetails ? addContactNameToCallHistory(contacts, [callDetails])[0]?.contact : callDetails?.contact,
     [contacts, callDetails]
   );
 
   const dispatch = useDispatch();
   const { addToast } = useToasts();

   const [editable, setEditable] = useState(true);

   useEffect(() => {
    const newIsEditable = () => {
      const canCall = getEditableForNumber(callDetails?.numberDialed, outgoingPhoneNumbers);
      return canCall;
    };
    setEditable(newIsEditable);
  }, [callDetails, outgoingPhoneNumbers]);
   
   useEffect(() => {
     loadBlockedNumbers();
     loadUserPermissions();
   }, []);
 
   const closeCreateContactDialog = useCallback(() => setIsCreateContactDialogOpen(false), []);

   // useEffect(() => {
   //   if (blockNumberStatus === ThunkStatus.ERROR) showToast(CANT_BLOCK_NUMBER_MESSAGE);
   //   if (blockNumberStatus === ThunkStatus.SUCCESS) showToastSuccess(BLOCK_NUMBER_SUCCESS);
   // }, [blockNumberStatus]);
 
   /**
    * Get a call ready. Pass the numberToCall, outgoingPhoneNumber to Keypad
    */
   const callBack = () => {
     if (callDetails) {
       const callerPhoneNumber = callDetails.getCallerPhoneNumber();
       const outgoingPhoneNumber = callDetails.getVirtualPhoneNumber();
 
       dispatch(setOutgoingPhoneNumber(outgoingPhoneNumber));
       dispatch(setNumberToCall(callerPhoneNumber));
 
       dispatch(push(routes.KEYPAD));
     }
   };
 
   /**
    * Create a new conversation to the callerPhoneNumber from virtualPhoneNumber
    */
   const textBack = () => {
     if (callDetails) {
       const callerPhoneNumber = trimPhoneNumber(callDetails.getCallerPhoneNumber());
       const outgoingPhoneNumber = trimPhoneNumber(callDetails.getVirtualPhoneNumber());
       
       dispatch(createNewConversation(outgoingPhoneNumber));
       dispatch(updateNewConversationPhoneNumber(callerPhoneNumber));
 
       dispatch(push(routes.TEXT_MESSAGES));
     }
   };
 
  /**
    * Block/unblock the call for phone number
    */
   const callBlock = () => {
 
     if (userPermissions) {
 
       const { itemBlockedNumber } = userPermissions;
 
       if (itemBlockedNumber === userConstants.permissions.EDIT && callDetails) {
         // Set the blocked numbers
         const phoneNumber = trimPhoneNumber(callDetails.getCallerPhoneNumber());  
         const blocked = blockedNumbers.some((number) => number === phoneNumber);
 
         if (blocked) {
           showToastSuccess(UNBLOCK_NUMBER_SUCCESS);
           return dispatch(unblockNumber(phoneNumber));
         }
 
         if (blockNumberStatus === ThunkStatus.ERROR) {
          showToast(CANT_BLOCK_NUMBER_MESSAGE);
         }
         else {
          showToastSuccess(BLOCK_NUMBER_SUCCESS);
        }
        return dispatch(blockNumber(phoneNumber));
       }
     }
 
     setIsShowBlockPermissionError(true);
   };
 
   /**
    * Draw the call back button
    */
   const callBackButton = () => {
     const enabled = outgoingPhoneNumbers.some(
       (element) => callDetails && trimPhoneNumber(String(callDetails.getVirtualPhoneNumber())) === trimPhoneNumber(String(element.getPhoneNumber()))
     );
     
     return (
       <Tooltip disabled={!enabled || !editable} title="Call">
         <IconButton
           id={domIds.CALL_HISTORY_CALLBACK_BUTTON}
           disabled={!enabled || !editable}
           onClick={() => callBack()}
           style={buttonStyles(!enabled || !editable)}
         >
           <CallBackIcon />
         </IconButton>
       </Tooltip>
     );
   };
 
   /**
    * Draw the text back button
    */
   const textBackButton = () => {
     const enabled = outgoingTextPhoneNumbers.some(
      (element) => callDetails && trimPhoneNumber(String(callDetails.getVirtualPhoneNumber())) === trimPhoneNumber(String(element.getPhoneNumber()))
      );
 
     return (
       <Tooltip disabled={!enabled} title="Text">
         <IconButton
           id={domIds.CALL_HISTORY_TEXTBACK_BUTTON}
           disabled={!enabled}
           onClick={() => textBack()}
           style={buttonStyles(!enabled)}
         >
           <TextBackIcon />
         </IconButton>
       </Tooltip>
     );
   };
 
   /**
    * Draw the add contact button 
    */
   const addContactButton = () => (
     <Tooltip 
       disabled={Boolean(callDetails?.contact)}
       title={ADD_CONTACT_TOOLTIP_LABEL}>
       <IconButton
         disabled={Boolean(callDetails?.contact)}
         onClick={() => setIsContactDialogOpen(true)}
         style={buttonStyles(Boolean(callDetails?.contact))}
       >
         <AddContactIcon />
       </IconButton>
     </Tooltip>
   );
 
   /**
    * Draw the create contact button 
    */
   const createContactButton = () => (
     <Tooltip title={attachedContact ? "Edit Contact" : "Create Contact"}>
       <IconButton
         onClick={() => setIsCreateContactDialogOpen(true)}
         style={buttonStyles()}
       >
         <CreateContactIcon style={{ width: 19, height: 19 }} />
       </IconButton>
     </Tooltip>
   );
   
   /**
    * Draw the call block button
    */
    const callBlockButton = () => {
     const blocked = callDetails && blockedNumbers.some(
       (number) => number === trimPhoneNumber(callDetails.getCallerPhoneNumber())
     );
 
     return (
       <Tooltip title={blocked ? "Unblock" : "Block"}>
         <IconButton
           id={domIds.CALL_HISTORY_BLOCK_UNBLOCK_BUTTON}
           onClick={() => callBlock()}
           style={buttonStyles()}
         >
           {blocked ? (
             <CallUnblockIcon id={domIds.CALL_HISTORY_UNBLOCK_ICON}/>
           ) : (
             <CallBlockIcon id={domIds.CALL_HISTORY_BLOCK_ICON} />
           )}
         </IconButton>
       </Tooltip>
     );
   };
 
   /**
    * Draw the header buttons
    */
   const drawHeaderButtons = () => (
     <div style={{ display: "flex", justifyContent: "flex-end" }}>
       {callBackButton()}
       {textBackButton()}
       {callBlockButton()}
       {!attachedContact && addContactButton()}
       {createContactButton()}
     </div>
   );
 
   /**
    * Load the blocked phone numbers
    */
   const loadBlockedNumbers = () => dispatch(fetchBlockedNumbers());
 
   /**
    * Request a getting user permissions
    */
   const loadUserPermissions = () => {
     // fix for unit tests - need check of authToken's dispatches
     getUserPermissions && getUserPermissions();
   };
 
   /**
    * Show the call block permission error dialog
    */
   const showBlockPermissionError = () => {
     if (isShowBlockPermissionError) {
       return (
         <ConfirmModal
           title={uiString.blockPermissionErrorModal.TITLE}
           desc={uiString.blockPermissionErrorModal.DESC}
           buttonLabel={uiString.OK}
           clickActionCallback={() => setIsShowBlockPermissionError(false)}
         />
       );
     }
 
     return null;
   };
 
   /**
    * Show a toast message
    *
    * @param message the message to display
    */
   const showToast = (message: string) => addToast(message, { appearance: "error", autoDismiss: true });
 
   /**
    * Show a toast message
    *
    * @param message the message to display
    */
    const showToastSuccess = (message: string) => addToast(message, { appearance: "success", autoDismiss: true });
 
   
     const leftColumnWidth = 7;
     const rightColumnWidth = 9;
     const contactName = callDetails?.contact?.name;
     const callResult = callDetails?.getCallResultFormatted();
     const typeFormatted = callDetails?.getCallTypeFormatted();
     const date = callDetails?.getSimpleFormattedDateAndTime();
     const callerPhoneNumber = callDetails?.getCallerPhoneNumberFormatted();
     const virtualPhoneNumber = callDetails?.getVirtualPhoneNumberFormatted();
     const memoizedDefaultContactData: IContact = useMemo(() => ({
       name: contactName ?? "",
       company: "",
       emails: [""],
       role: "",
       url: "",
       notes: "",
       id: callDetails && callDetails.id ? String(callDetails.id) : "",
       phoneNumbers: [{
         phoneNumber: String(callerPhoneNumber),
         type: ContactPhoneTypeEnum.MOBILE,    
         id: "",
       }],
     }), [contactName, callerPhoneNumber])
 
     if (!callDetails) {
      return (
        <CallDetails
          title={uiString.CALL_DETAILS}
          relatedCalls={relatedCalls ?? []}
          parent={parent}
          hideDetails={() => dispatch(hideCallDetails(parent))}
          titleBlockStyle={{ marginTop: 10}}
        />
      );
    }
  
   return (
     <CallDetails
       key={`call-history-details-${callDetails.id}`}
       title={uiString.CALL_DETAILS}
       relatedCalls={relatedCalls ?? []}
       parent={parent}
       hideDetails={() => dispatch(hideCallDetails(parent))}
       buttons={drawHeaderButtons()}
       titleBlockStyle={{ marginTop: 10}}
     >
       {showBlockPermissionError()}
       <List relaxed>
         <List.Item>
           <Grid>
             <Grid.Column
               className="text-normal none-select-text"
               width={leftColumnWidth}
             >
               {`${uiString.CALLER_NUMBER}: `}
             </Grid.Column>
             <Grid.Column
               textAlign="right"
               className="text-light"
               width={rightColumnWidth}
             >
               {callerPhoneNumber}
             </Grid.Column>
           </Grid>
         </List.Item>
         <List.Item style={{ paddingTop: "10px" }}>
           <Grid>
             <Grid.Column
               className="text-normal none-select-text"
               width={leftColumnWidth}
             >
               {`${uiString.VIRTUAL_PHONE_NUMBER}: `}
             </Grid.Column>
             <Grid.Column
               textAlign="right"
               className="text-light"
               width={rightColumnWidth}
             >
               {virtualPhoneNumber}
             </Grid.Column>
           </Grid>
         </List.Item>
         <List.Item style={{ paddingTop: "10px" }}>
           <Grid>
             <Grid.Column
               className="text-normal none-select-text"
               width={leftColumnWidth}
             >
               {`${uiString.DATE_AND_TIME}: `}
             </Grid.Column>
             <Grid.Column
               textAlign="right"
               className="text-light"
               width={rightColumnWidth}
             >
               {date}
             </Grid.Column>
           </Grid>
         </List.Item>
         <List.Item style={{ paddingTop: "10px" }}>
           <Grid>
             <Grid.Column
               className="text-normal none-select-text"
               width={leftColumnWidth}
             >
               {`${uiString.DURATION}: `}
             </Grid.Column>
             <Grid.Column
               textAlign="right"
               className="text-light"
               width={rightColumnWidth}
             >
               {formatSecondsWithQualifier(callDetails.duration.toString())}
             </Grid.Column>
           </Grid>
         </List.Item>
         <List.Item style={{ paddingTop: "10px" }}>
           <Grid>
             <Grid.Column
               className="text-normal none-select-text"
               width={leftColumnWidth}
             >
               {`${uiString.DIRECTION}: `}
             </Grid.Column>
             <Grid.Column
               textAlign="right"
               className="text-light"
               width={rightColumnWidth}
             >
               {typeFormatted}
             </Grid.Column>
           </Grid>
         </List.Item>
 
         <RenderCallStatus callResult={callResult} type={callDetails.type} />
       </List>
       <ContactDialog 
           open={isContactDialogOpen}
           onClose={() => setIsContactDialogOpen(false)}
           phoneNumber={callDetails.getCallerPhoneNumber()}
         />
         <CreateContactDialog
           open={isCreateContactDialogOpen}
           onClose={closeCreateContactDialog}
           contactData={!attachedContact ? memoizedDefaultContactData : attachedContact}
         />
     </CallDetails>
   );
 }
 /**
  * Display the call status
  * @param {*} props
  */
 function RenderCallStatus({ callResult, type }) {
   if (type === CallType.INBOUND) {
     return (
       <List.Item style={{ paddingTop: "10px" }}>
         <Grid>
           <Grid.Column className="text-normal" width={7}>
             {`${uiString.STATUS}: `}
           </Grid.Column>
           <Grid.Column textAlign="right" className="text-light" width={9}>
             {callResult}
           </Grid.Column>
         </Grid>
       </List.Item>
     );
   }
 
   return null;
 }
 
 export default CallHistoryDetails;